import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";

const BG_URL =
  "https://static.prod-images.emergentagent.com/jobs/ba471818-25b7-4add-86ea-566934dc4519/images/454a54197df03f692ddc3e736d680456916b7a16700d6a7e6746656dfa65ac86.png";

export default function Login() {
  const { user, login, isAdmin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to={isAdmin ? "/admin" : "/account"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(res.user.role === "admin" ? "/admin" : "/account");
    else setError(res.error || "Login failed");
  };

  return (
    <div
      className="min-h-screen w-full flex relative font-sans"
      style={{ backgroundImage: `url(${BG_URL})`, backgroundSize: "cover", backgroundPosition: "center" }}
    >
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative z-10 w-full flex items-center justify-center md:justify-start px-6 md:px-20">
        <div className="rounded-lg w-full max-w-md p-8 dark text-white bg-zinc-950 border border-white/10 shadow-2xl" data-testid="login-card">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-md bg-white text-black flex items-center justify-center font-heading font-black">
              S
            </div>
            <div>
              <h1 className="font-heading font-black text-xl leading-none">premiumtools.store</h1>
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/60 mt-0.5">
                Sign in
              </p>
            </div>
          </Link>
          <h2 className="font-heading text-2xl font-bold mb-1">Welcome back.</h2>
          <p className="text-sm text-white/60 mb-6">Login to manage your subscriptions.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-white/70">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/5 border-white/15 text-white placeholder:text-white/40"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-white/70">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/15 text-white placeholder:text-white/40"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-white text-black hover:bg-white/90">
              <Lock className="w-4 h-4 mr-2" />
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <p className="text-sm text-white/60 mt-6">
            New here?{" "}
            <Link to="/signup" data-testid="goto-signup" className="text-white underline">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
